// Las acciones del panel de gestión salen del GRAFO de la receta para el
// carril del usuario (`legalObservationTargets`), jamás de un mapa
// estado→botones (ADR-0105 §Consequences). Este módulo clasifica cada
// destino por su FORMA, nunca por su clave literal:
//   - asignar   = el destino que sólo puede fijar el carril assigning_party
//                 y que no es terminal (facility, ADR-0134: «anulada»
//                 también es sólo de assigning_party, pero es un callejón
//                 sin aristas, así que es un cierre, no una asignación);
//   - rechazar  = el destino terminal sin aristas («Rechazar» en
//                 postventa, «Anular» en facility: el verbo sale de la
//                 etiqueta del destino, ver `rejectWords`);
//   - aceptar   = el destino desde el cual assigning_party alcanza la
//                 asignación (la fase «por asignar»).
// Además `assignReachable`: si ESTE carril llega a la asignación, directo
// o a través de la aceptación. Desde el triage `/assign` acepta y asigna
// en una sola transacción (findings.ts, `assignTargetResolvers`); es lo
// que habilita «Reasignar» en una observación reabierta por no conforme.
//
// El veredicto (Conforme / No conforme) NO es una arista que el panel
// recorra con /observation-status: es POST /findings/:id/conformity, y lo
// da sólo un carril de cierre (`observationClosure.closedBy`). Postventa
// cierra sólo con asset_party (el propietario, desde la PWA), así que su
// panel no tiene veredicto; facility cierra también con assigning_party
// (supervisor / gerente, ADR-0134 §1). Ver `resolveVerdictActions`.

import {
  closureLanes,
  legalObservationTargets,
  notConformEvidenceRequired,
  type ObservationPhaseVocabulary,
  type ObservationStatusActor,
} from "@bitacorapro/core-engine";

type Vocab = ObservationPhaseVocabulary;
export type GestionTarget = ReturnType<typeof legalObservationTargets>[number];

export type GestionActions = {
  acceptTarget: GestionTarget | null;
  rejectTarget: GestionTarget | null;
  assignTarget: GestionTarget | null;
  /** El carril alcanza la asignación desde aquí: directo (`assignTarget`)
   *  o pasando por la aceptación, que `/assign` compone en servidor. */
  assignReachable: boolean;
};

const NONE: GestionActions = {
  acceptTarget: null,
  rejectTarget: null,
  assignTarget: null,
  assignReachable: false,
};

// ADR-0141: «Rechazar»/«Anular» se declara en la receta (rejectShaped),
// no se deriva de la forma — con el grafo nuevo «terminal sin aristas»
// también calza rechazada_cerrada, que es el cierre de la respuesta del
// propietario, no un botón del panel.
//
// Respaldo de skew de deploy (dashboard de Cloudflare publicado antes
// que el core-api de Railway, o un rollback del backend): una receta
// anterior a ADR-0141 no declara `rejectShaped` en NINGÚN estado, y sin
// respaldo los botones «Rechazar»/«Anular» desaparecerían durante toda
// la ventana. El respaldo es la forma antigua (terminal sin aristas) y
// se decide POR RECETA, no por destino: en la receta nueva el flag está
// deliberadamente ausente en rechazada_cerrada, y un respaldo por
// destino la leería como botón.
export function rejectShapedPredicate(recipe: Vocab): (t: GestionTarget) => boolean {
  const declared = recipe.observationStatuses.some((s) => s.rejectShaped !== undefined);
  return declared
    ? (t) => t.rejectShaped === true
    : (t) => t.terminal && t.transitionsTo.length === 0;
}

/** `lane` null, `share_link` o `asset_party` → sin acciones: esos
 *  carriles no operan el panel del dashboard (el jefe de tienda de
 *  facility resuelve con fotos, desde la PWA). */
export function resolveGestionActions(
  recipe: Vocab | undefined,
  status: string | null,
  lane: ObservationStatusActor | null
): GestionActions {
  if (!recipe || !lane || lane === "share_link" || lane === "asset_party") return NONE;
  const isRejectShaped = rejectShapedPredicate(recipe);
  const isAssignShaped = (t: GestionTarget) =>
    !isRejectShaped(t) && t.settableBy.length === 1 && t.settableBy[0] === "assigning_party";
  const targets = legalObservationTargets(recipe, status, lane);
  const rejectTarget = targets.find(isRejectShaped) ?? null;
  const assignTarget = targets.find(isAssignShaped) ?? null;
  // Fuera del origen del cierre (lo resuelto) no se acepta ni se asigna
  // «de pasada»: la arista que sale de ahí es la No conformidad, y es del
  // veredicto (POST /conformity), no del panel — mismo criterio que
  // `conformityRouteOwns` del engine. Sin esto, la arista resuelta →
  // aceptada de facility (ADR-0134) se leía como «aceptar» y le daba un
  // «Asignar» a una observación que espera revisión.
  if (status !== null && status === recipe.observationClosure?.fromStatus) {
    return {
      acceptTarget: null,
      rejectTarget,
      assignTarget,
      assignReachable: assignTarget !== null,
    };
  }
  // Aceptar nunca es «el primer target del techo»: con un estado NULL o
  // retirado los targets son el techo completo del carril (incluida la
  // propia entrada), y elegir el primero mandaba la fila de vuelta a
  // triage. Sin un destino así no hay botón Aceptar.
  const acceptTarget =
    targets.find(
      (t) =>
        !isAssignShaped(t) &&
        !isRejectShaped(t) &&
        t.key !== status &&
        legalObservationTargets(recipe, t.key, "assigning_party").some(isAssignShaped)
    ) ?? null;
  // Mismo predicado que el servidor (`destinoDeAceptacion`): un destino,
  // distinto del propio estado, desde el cual ESTE carril asigna.
  const assignReachable =
    assignTarget !== null ||
    targets.some(
      (t) => t.key !== status && legalObservationTargets(recipe, t.key, lane).some(isAssignShaped)
    );
  return { acceptTarget, rejectTarget, assignTarget, assignReachable };
}

export type AppealActions = {
  /** «Acoger apelación»: el destino que devuelve la observación al cauce
   *  (no es un cierre). Null si el grafo no lo da para este carril. */
  acogerTarget: GestionTarget | null;
  /** «Mantener el rechazo»: el destino cerrado (sin aristas de salida).
   *  La nota es obligatoria (la arista la exige; el panel la pide). */
  mantenerTarget: GestionTarget | null;
};

const NO_APPEAL: AppealActions = { acogerTarget: null, mantenerTarget: null };

/** ADR-0141 — la resolución de una apelación, derivada del grafo para el
 *  carril (`legalObservationTargets`), nunca de un mapa estado→botones:
 *  desde el estado apelado, el destino SIN aristas de salida es mantener
 *  el rechazo y el que sigue vivo es acoger. Quien llama decide CUÁNDO
 *  (kind `appealed`); esto decide QUÉ ofrece el grafo. */
export function resolveAppealActions(
  recipe: Vocab | undefined,
  status: string | null,
  lane: ObservationStatusActor | null
): AppealActions {
  if (!recipe || !lane || lane === "share_link" || lane === "asset_party") return NO_APPEAL;
  const targets = legalObservationTargets(recipe, status, lane);
  const mantenerTarget = targets.find((t) => t.transitionsTo.length === 0) ?? null;
  const acogerTarget = targets.find((t) => t.transitionsTo.length > 0) ?? null;
  return { acogerTarget, mantenerTarget };
}

export type VerdictActions = {
  /** Puede dar Conforme: es carril de cierre. */
  conforme: boolean;
  /** Puede dar No conforme DESDE EL DASHBOARD. Un carril que debe la
   *  constancia completa (foto + nota de voz, ADR-0134 §5) no puede: el
   *  dashboard no graba voz ni sube fotos de hallazgos, así que ese botón
   *  se oculta (fundador 2026-10-03: ocultar lo que no tiene uso) y esa
   *  No conformidad se da desde la PWA. */
  noConforme: boolean;
};

const NO_VERDICT: VerdictActions = { conforme: false, noConforme: false };

type VerdictVocab = ObservationPhaseVocabulary;

/** El veredicto sobre una observación resuelta. Quien llama decide CUÁNDO
 *  (la fase awaiting_receiver); esto decide QUIÉN. */
export function resolveVerdictActions(
  recipe: VerdictVocab | undefined,
  lane: ObservationStatusActor | null
): VerdictActions {
  if (!recipe || !lane || lane === "share_link") return NO_VERDICT;
  if (!closureLanes(recipe).includes(lane)) return NO_VERDICT;
  return { conforme: true, noConforme: !notConformEvidenceRequired(recipe, lane) };
}

/** Cómo se asigna en esta receta. Con taxonomía (ADR-0129, postventa) la
 *  asignación ES la clasificación de especialidad y el panel la pide en
 *  línea; sin taxonomía (facility) se asigna con el flujo de #0431:
 *  elegir o dar de alta al proveedor y confirmar el destinatario. */
export type AssignFlavor = "classify_and_assign" | "pick_and_confirm";

export function assignFlavor(recipe: { observationTaxonomy?: unknown } | undefined): AssignFlavor {
  return recipe?.observationTaxonomy ? "classify_and_assign" : "pick_and_confirm";
}

// El sustantivo del cierre sin trabajo es castellano, no vocabulario de
// receta: un verbo que no está acá cae a «Motivo» + «Confirmar».
const SUSTANTIVO: Record<string, string> = {
  rechazar: "rechazo",
  anular: "anulación",
  descartar: "descarte",
  cancelar: "cancelación",
};

export type RejectWords = {
  /** «Rechazar» / «Anular». */
  verb: string;
  /** «rechazada» / «anulada». */
  participle: string;
  /** «Motivo del rechazo» / «Motivo de la anulación». */
  reasonLabel: string;
  /** «Confirmar rechazo» / «Confirmar anulación». */
  confirm: string;
};

/** Las palabras del cierre sin trabajo, desde la etiqueta del destino:
 *  «Rechazada» → «Rechazar» / «rechazo», «Anulada» → «Anular» /
 *  «anulación». */
export function rejectWords(target: Pick<GestionTarget, "label">): RejectWords {
  const participle = target.label.toLowerCase();
  const stem = participle.replace(/ada$/, "ar").replace(/ida$/, "ir");
  const verb = stem.charAt(0).toUpperCase() + stem.slice(1);
  const noun = SUSTANTIVO[stem];
  const art = noun?.endsWith("ción") ? "de la" : "del";
  return {
    verb,
    participle,
    reasonLabel: noun ? `Motivo ${art} ${noun}` : "Motivo",
    confirm: noun ? `Confirmar ${noun}` : "Confirmar",
  };
}
